"use client";

import { ContactBlockElementModel } from "api/umbraco";
import Button from "components/atoms/ButtonComponent/Button";
import { useAnalytics } from "providers/AnalyticsProvider";
import { useState, useRef } from "react";
import { useGTMEvent } from "utils/useGTMEvent";
import * as CookieConsent from "vanilla-cookieconsent";
declare const grecaptcha: {
  execute(siteKey: string, options: { action: string }): Promise<string>;
};
export default function ContactFormBlockComponent({
  content,
}: {
  content: ContactBlockElementModel;
}) {
  const { trackEvent, trackConversion } = useAnalytics();
  const { pushEvent } = useGTMEvent();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null); // State for success/error messages
  const formRef = useRef<HTMLFormElement>(null);
  if (!content) {
    return null;
  }

  const validateMobile = (mobile: string): boolean => {
    // Remove all spaces and non-numeric characters except +
    const cleaned = mobile.replace(/[\s\-()]/g, "");

    // Danish mobile number patterns
    const patterns = [
      /^[0-9]{8}$/, // 12345678
      /^\+45[0-9]{8}$/, // +4512345678
    ];

    return patterns.some((pattern) => pattern.test(cleaned));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null); // Clear any previous messages

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      mobile: formData.get("mobile") as string,
      message: formData.get("problem") as string,
    };

    // Validate mobile number
    if (data.mobile && !validateMobile(data.mobile)) {
      setMessage(
        "Indtast venligst et gyldigt dansk mobilnummer (f.eks. 12345678 eller +45 12345678)"
      );
      setLoading(false);
      return;
    }

    try {
      const marketingConsent =
        typeof window !== "undefined" &&
        (window.CookieConsent as typeof CookieConsent)?.acceptedCategory?.(
          "necessary"
        );
      if (!marketingConsent) {
        throw new Error("Samtykke til marketingcookies er ikke givet.");
      }
      if (!grecaptcha) {
        throw new Error("reCAPTCHA is not loaded");
      }

      const recaptchaToken = await grecaptcha.execute(
        process.env.NEXT_APP_RECAPTCHA_SITE_KEY!,
        { action: "submit" }
      );

      const payload = {
        ...data,
        recaptchaToken,
      };
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        console.error("API Error:", response.status, errorData);
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Success:", result);

      setMessage("Beskeden er sendt!"); // Show success message
      const form = formRef.current;
      if (form) {
        form.reset(); // Clear form fields
      } else {
        console.error("Form element is null");
      }
      // ✅ Trigger GA4 tracking only after success
      trackEvent("form_submit", {
        label: "Contact Form",
        section: "Contact Block",
      });
      pushEvent("form_submit", {
        form_name: "contact",
        submission_type: "appointment_request",
      });
      trackConversion("contact_form_submitted", 0);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(error);
        setMessage(
          error.message || "Der opstår et problem. beskeden er ikke sendt."
        ); // Show error message
      } else {
        console.error("An unknown error occurred");
        setMessage("Der opstår et problem. beskeden er ikke sendt."); // Show generic error message
      }
    } finally {
      setLoading(false);
    }
  };

  // const properties = content.properties as ContactFormBlockPropertiesModel;

  return (
    <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.5s">
      <div className="bg-light rounded h-100 d-flex align-items-center p-5">
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-12 col-sm-12">
              <input
                name="name"
                type="text"
                className="form-control border-0"
                placeholder="Dit navn"
                style={{ height: "55px" }}
                required
              />
            </div>
            <div className="col-12 col-sm-6">
              <input
                name="mobile"
                type="tel"
                className="form-control border-0"
                placeholder="Mobilnummer"
                style={{ height: "55px" }}
                pattern="^(\+45\s?)?[0-9]{2}\s?[0-9]{2}\s?[0-9]{2}\s?[0-9]{2}$|^(\+45\s?)?[0-9]{8}$"
                required
                inputMode="tel"
                autoComplete="tel"
                title="Indtast et gyldigt dansk mobilnummer (f.eks. 12345678, 12 34 56 78, +45 12345678)"
              />
            </div>
            <div className="col-12 col-sm-6">
              <input
                name="email"
                type="email"
                className="form-control border-0"
                placeholder="Email"
                style={{ height: "55px" }}
                required
              />
            </div>
            <div className="col-12">
              <textarea
                name="problem"
                className="form-control border-0"
                rows={5}
                placeholder="Besked"
              ></textarea>
            </div>
            <div className="col-12">
              <Button
                type="submit"
                className="btn btn-primary w-100 py-3"
                disabled={loading}
              >
                {loading ? "Sender..." : "Send Besked"}
              </Button>
            </div>
          </div>
          {message && (
            <div
              className={`alert mt-3 ${
                message.includes("sendt") ? "alert-success" : "alert-danger"
              }`}
            >
              {message}
            </div>
          )}
          <div className="row g-3 mt-3">
            <div className="recaptcha-disclaimer">
              Denne hjemmeside er beskyttet af reCAPTCHA, og Googles{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="border-bottom"
                title="Privatlivspolitik"
              >
                Privatlivspolitik
              </a>{" "}
              og{" "}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="border-bottom"
                title="Servicevilkår"
              >
                Servicevilkår
              </a>{" "}
              er gældende.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
